import React, { useState, useEffect } from 'react';
import { instrumentosAPI } from '../../services/api';
import { validateEmail, validatePhone } from '../../services/utils';

const StudentForm = ({ student, onSubmit, onCancel, currentYear }) => {
  const [formData, setFormData] = useState({
    Nombre: '',
    Apellido1: '',
    Apellido2: '',
    Telefono1: '',
    Telefono2: '',
    Email1: '',
    Email2: '',
    F_Nacimiento: '',
    InstrumentoID1: '',
    InstrumentoID2: '',
    F_Alta_ME: '',
    F_Baja_ME: '',
    Curso: ''
  });

  const [instruments, setInstruments] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchInstruments();
    if (student) {
      setFormData({
        ...student,
        Nombre: (student.Nombre || '').toUpperCase(),
        Apellido1: (student.Apellido1 || '').toUpperCase(),
        Apellido2: (student.Apellido2 || '').toUpperCase(),
        Email1: (student.Email1 || '').toLowerCase(),
        Email2: (student.Email2 || '').toLowerCase(),
        Curso: String(student.Curso || '').replace(/\D/g, '').slice(0, 4),
        F_Nacimiento: student.F_Nacimiento?.split('T')[0] || '',
        F_Alta_ME: student.F_Alta_ME?.split('T')[0] || '',
        F_Baja_ME: student.F_Baja_ME?.split('T')[0] || ''
      });
    } else {
      // Al crear, preseleccionar el curso con el año seleccionado en la cabecera
      setFormData(prev => ({ ...prev, Curso: currentYear ? String(currentYear) : '' }));
    }
  }, [student, currentYear]);

  const fetchInstruments = async () => {
    try {
      const response = await instrumentosAPI.getAll();
      setInstruments(response.data);
    } catch (error) {
      console.error('Error fetching instruments:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    if (name === 'Nombre' || name === 'Apellido1' || name === 'Apellido2') {
      processedValue = value.toUpperCase();
    }

    if (name === 'Email1' || name === 'Email2') {
      processedValue = value.toLowerCase();
    }

    if (name === 'Curso') {
      processedValue = value.replace(/\D/g, '').slice(0, 4);
    }
    
    setFormData(prev => ({ ...prev, [name]: processedValue }));
    
    // Validación en tiempo real
    if (name === 'Email1' && processedValue && !validateEmail(processedValue)) {
      setErrors(prev => ({ ...prev, Email1: 'Email inválido' }));
    } else if (name === 'Email2' && processedValue && !validateEmail(processedValue)) {
      setErrors(prev => ({ ...prev, Email2: 'Email inválido' }));
    } else if (name === 'Telefono1' && processedValue && !validatePhone(processedValue)) {
      setErrors(prev => ({ ...prev, Telefono1: 'Teléfono inválido (9 dígitos)' }));
    } else if (name === 'Telefono2' && processedValue && !validatePhone(processedValue)) {
      setErrors(prev => ({ ...prev, Telefono2: 'Teléfono inválido (9 dígitos)' }));
    } else if (name === 'Curso' && processedValue && processedValue.length !== 4) {
      setErrors(prev => ({ ...prev, Curso: 'Curso debe tener 4 dígitos numéricos' }));
    } else {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const newErrors = {};
    
    // Validaciones requeridas
    if (!formData.Nombre || !formData.Nombre.trim()) newErrors.Nombre = 'Nombre es requerido';
    if (!formData.Apellido1 || !formData.Apellido1.trim()) newErrors.Apellido1 = 'Primer apellido es requerido';
    if (!formData.Telefono1 || !formData.Telefono1.trim()) newErrors.Telefono1 = 'Teléfono es requerido';
    else if (!validatePhone(formData.Telefono1.trim())) newErrors.Telefono1 = 'Teléfono inválido (9 dígitos)';

    if (formData.Telefono2 && formData.Telefono2.trim() && !validatePhone(formData.Telefono2.trim())) {
      newErrors.Telefono2 = 'Teléfono inválido (9 dígitos)';
    }
    
    if (!formData.Email1 || !formData.Email1.trim()) newErrors.Email1 = 'Email es requerido';
    else if (!validateEmail(formData.Email1.trim())) newErrors.Email1 = 'Email inválido';
    
    if (formData.Email2 && formData.Email2.trim() && !validateEmail(formData.Email2.trim())) {
      newErrors.Email2 = 'Email inválido';
    }
    
    if (!formData.F_Nacimiento) newErrors.F_Nacimiento = 'Fecha de nacimiento es requerida';
    if (!formData.F_Alta_ME) newErrors.F_Alta_ME = 'Fecha de alta en Escuela de Música es requerida';

    // El curso solo es obligatorio al crear (crea la matrícula inicial del alumno)
    if (!student && (!formData.Curso || !/^\d{4}$/.test(formData.Curso))) {
      newErrors.Curso = 'Curso debe ser numérico de 4 dígitos';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // Preparar datos para enviar, asegurando tipos correctos
    const dataToSubmit = {
      Nombre: (formData.Nombre || '').trim().toUpperCase(),
      Apellido1: (formData.Apellido1 || '').trim().toUpperCase(),
      Apellido2: (formData.Apellido2 || '').trim().toUpperCase(),
      Telefono1: (formData.Telefono1 || '').trim(),
      Telefono2: (formData.Telefono2 || '').trim(),
      Email1: (formData.Email1 || '').trim().toLowerCase(),
      Email2: (formData.Email2 || '').trim().toLowerCase(),
      F_Nacimiento: formData.F_Nacimiento || null,
      F_Alta_ME: formData.F_Alta_ME || null,
      F_Baja_ME: formData.F_Baja_ME || null,
      InstrumentoID1: formData.InstrumentoID1 ? parseInt(formData.InstrumentoID1) : null,
      InstrumentoID2: formData.InstrumentoID2 ? parseInt(formData.InstrumentoID2) : null
    };

    // El curso solo se envía al crear (el backend crea la matrícula del año); al editar no se toca.
    if (!student) {
      dataToSubmit.Curso = String(formData.Curso || '').replace(/\D/g, '').slice(0, 4);
    }

    onSubmit(dataToSubmit);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Nombre */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Nombre *
          </label>
          <input
            type="text"
            name="Nombre"
            value={formData.Nombre}
            onChange={handleChange}
            className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm ${
              errors.Nombre ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
            }`}
          />
          {errors.Nombre && (
            <p className="mt-1 text-sm text-red-600">{errors.Nombre}</p>
          )}
        </div>

        {/* Apellido1 */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Primer Apellido *
          </label>
          <input
            type="text"
            name="Apellido1"
            value={formData.Apellido1}
            onChange={handleChange}
            className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm ${
              errors.Apellido1 ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
            }`}
          />
          {errors.Apellido1 && (
            <p className="mt-1 text-sm text-red-600">{errors.Apellido1}</p>
          )}
        </div>

        {/* Apellido2 */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Segundo Apellido
          </label>
          <input
            type="text"
            name="Apellido2"
            value={formData.Apellido2}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          />
        </div>

        {/* Telefono1 */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Teléfono Principal *
          </label>
          <input
            type="tel"
            name="Telefono1"
            value={formData.Telefono1}
            onChange={handleChange}
            className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm ${
              errors.Telefono1 ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
            }`}
          />
          {errors.Telefono1 && (
            <p className="mt-1 text-sm text-red-600">{errors.Telefono1}</p>
          )}
        </div>

        {/* Telefono2 */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Teléfono Secundario
          </label>
          <input
            type="tel"
            name="Telefono2"
            value={formData.Telefono2}
            onChange={handleChange}
            className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm ${
              errors.Telefono2 ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
            }`}
          />
          {errors.Telefono2 && (
            <p className="mt-1 text-sm text-red-600">{errors.Telefono2}</p>
          )}
        </div>

        {/* Email1 */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Email Principal *
          </label>
          <input
            type="email"
            name="Email1"
            value={formData.Email1}
            onChange={handleChange}
            className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm ${
              errors.Email1 ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
            }`}
          />
          {errors.Email1 && (
            <p className="mt-1 text-sm text-red-600">{errors.Email1}</p>
          )}
        </div>

        {/* Email2 */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Email Secundario
          </label>
          <input
            type="email"
            name="Email2"
            value={formData.Email2}
            onChange={handleChange}
            className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm ${
              errors.Email2 ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
            }`}
          />
          {errors.Email2 && (
            <p className="mt-1 text-sm text-red-600">{errors.Email2}</p>
          )}
        </div>

        {/* F_Nacimiento */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Fecha de Nacimiento *
          </label>
          <input
            type="date"
            name="F_Nacimiento"
            value={formData.F_Nacimiento}
            onChange={handleChange}
            className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm ${
              errors.F_Nacimiento ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
            }`}
          />
          {errors.F_Nacimiento && (
            <p className="mt-1 text-sm text-red-600">{errors.F_Nacimiento}</p>
          )}
        </div>

        {/* Curso (solo al crear: define la matrícula inicial del alumno) */}
        {!student && (
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Curso *
          </label>
          <input
            type="text"
            name="Curso"
            value={formData.Curso}
            onChange={handleChange}
            className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm ${
              errors.Curso ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
            }`}
          />
          {errors.Curso && (
            <p className="mt-1 text-sm text-red-600">{errors.Curso}</p>
          )}
        </div>
        )}

        {/* InstrumentoID1 */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Instrumento Principal
          </label>
          <select
            name="InstrumentoID1"
            value={formData.InstrumentoID1}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          >
            <option value="">Seleccionar instrumento</option>
            {instruments.map(instrument => (
              <option key={instrument.IDInstrumento} value={instrument.IDInstrumento}>
                {instrument.Nombre}
              </option>
            ))}
          </select>
        </div>

        {/* InstrumentoID2 */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Instrumento Secundario
          </label>
          <select
            name="InstrumentoID2"
            value={formData.InstrumentoID2}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          >
            <option value="">Seleccionar instrumento</option>
            {instruments.map(instrument => (
              <option key={instrument.IDInstrumento} value={instrument.IDInstrumento}>
                {instrument.Nombre}
              </option>
            ))}
          </select>
        </div>

        {/* F_Alta_ME */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Fecha de Alta en Escuela de Música *
          </label>
          <input
            type="date"
            name="F_Alta_ME"
            value={formData.F_Alta_ME}
            onChange={handleChange}
            className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm ${
              errors.F_Alta_ME ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
            }`}
          />
          {errors.F_Alta_ME && (
            <p className="mt-1 text-sm text-red-600">{errors.F_Alta_ME}</p>
          )}
        </div>

        {/* F_Baja_ME */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Fecha de Baja en Escuela de Música
          </label>
          <input
            type="date"
            name="F_Baja_ME"
            value={formData.F_Baja_ME}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          />
        </div>
      </div>

      {/* Botones */}
      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          Cancelar
        </button>
        <button
          type="submit"
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          {student ? 'Actualizar' : 'Guardar'}
        </button>
      </div>
    </form>
  );
};

export default StudentForm;